// src/pages/PaymentCallbackPage.jsx
// ================================================================
//  صفحه بازگشت از درگاه پرداخت — نسخه نهایی و کامل
//  ----------------------------------------------------------------
//  • تأیید تراکنش از سمت سرور (verifyPayment)
//  • اجرای fulfillment بر اساس purpose (idempotent + امن)
//  • پشتیبانی کامل از همه‌ی purposeها (canonical + alias):
//      - coins              → افزودن سکه و XP
//      - ai_subscription    → اشتراک AI (aiQuotaService) + legacy
//      - workbook           → لایسنس کتاب کار + library
//      - lesson_subscription→ اشتراک درس (subscriptionManager)
//      - step_quiz_unlock   → آنلاک آزمون مرحله (progressManager)
//      - course             → خرید دوره
//      - jozve              → جزوه ویژه (jozveService)
//      - free_study         → مطالعه آزاد
//      - wallet_charge      → شارژ کیف پول (فقط refresh)
//      - private_class      → کلاس خصوصی
//      - custom             → پرداخت عمومی
//  • هدایت خودکار به مقصد مناسب
// ================================================================

import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  verifyPayment,
  parseCallbackParams,
  PAYMENT_PURPOSE,
} from "../services/paymentService";
import { workbookService } from "../services/workbookService";
import {
  activateSubscription as activateAISubscription,
} from "../services/aiQuotaService";
import {
  setSubscription as setAIChatSubscription,
} from "../core/ai/subscription";
import {
  subscriptionManager,
  progressManager,
} from "../core/books/logic.js";
import { engine } from "../core/books/storage.js";
import { useAuth } from "../context/AuthContext";
import { useCoins } from "../context/CoinContext";
import "./PaymentCallbackPage.css";

// ================================================================
// ICONS
// ================================================================

const IconCheck = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M20 6 9 17l-5-5" />
  </svg>
);

const IconCross = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const IconShield = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z" />
    <path d="m9 12 2 2 4-4" />
  </svg>
);

const IconHome = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M3 10.5 12 3l9 7.5" />
    <path d="M5 10v10h14V10" />
  </svg>
);

const IconBack = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M9 18l6-6-6-6" />
  </svg>
);

const IconReceipt = () => (
  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
    <path d="M6 2h12a2 2 0 0 1 2 2v18l-3-2-3 2-3-2-3 2-3-2V4a2 2 0 0 1 2-2z" />
    <path d="M8 8h8M8 12h8M8 16h5" />
  </svg>
);

// ================================================================
// PURPOSE LABELS (شامل همه‌ی مقادیر canonical + alias)
// ================================================================

const PURPOSE_LABELS = Object.freeze({
  // Canonical
  [PAYMENT_PURPOSE.COINS]:               "خرید سکه",
  [PAYMENT_PURPOSE.AI_SUBSCRIPTION]:     "اشتراک AI",
  [PAYMENT_PURPOSE.WORKBOOK]:            "کتاب کار",
  [PAYMENT_PURPOSE.JOZVE]:               "جزوه ویژه",
  [PAYMENT_PURPOSE.LESSON_SUBSCRIPTION]: "اشتراک درس",
  [PAYMENT_PURPOSE.FREE_STUDY]:          "دوره مطالعه آزاد",
  [PAYMENT_PURPOSE.STEP_QUIZ_UNLOCK]:    "آنلاک آزمون مرحله",
  [PAYMENT_PURPOSE.WALLET_CHARGE]:       "شارژ کیف پول",
  [PAYMENT_PURPOSE.PRIVATE_CLASS]:       "کلاس خصوصی",
  [PAYMENT_PURPOSE.COURSE]:              "خرید دوره",
  [PAYMENT_PURPOSE.CUSTOM]:              "پرداخت",

  // Aliases (برای سازگاری)
  [PAYMENT_PURPOSE.SUBSCRIPTION]:  "اشتراک ویژه",
  [PAYMENT_PURPOSE.BOOK]:          "خرید کتاب",
  [PAYMENT_PURPOSE.EXAM]:          "خرید آزمون",
  [PAYMENT_PURPOSE.WALLET]:        "شارژ کیف پول",
});

const getPurposeLabel = (purpose) =>
  PURPOSE_LABELS[purpose] || "پرداخت";

const formatAmount = (amount) => {
  const n = Number(amount);
  if (!Number.isFinite(n) || n <= 0) return "—";
  return `${n.toLocaleString("fa-IR")} تومان`;
};

// ================================================================
// 🎯 HELPER: نرمال‌سازی purpose (alias → canonical)
// ----------------------------------------------------------------
// این تابع تضمین می‌کند که switch روی مقادیر canonical اجرا شود
// و از تکرار case در switch جلوگیری می‌کند.
// ================================================================

function normalizePurpose(purpose) {
  if (!purpose) return purpose;
  const map = {
    // aliases → canonical
    subscription: "ai_subscription",
    book:         "workbook",
    exam:         "step_quiz_unlock",
    wallet:       "wallet_charge",
  };
  return map[purpose] || purpose;
}

// ================================================================
// 🎯 FULFILLMENT ENGINE
// ----------------------------------------------------------------
// تمام منطق اجرای اثر خرید اینجا متمرکز است.
// این تابع idempotent است — اگر دوبار صدا زده شود، اثر دوباره
// اعمال نمی‌شود (به‌کمک سرور + duplicate_tx + Set-like checks).
// ================================================================

async function runFulfillment(res, ctx) {
  const { user, refreshUser, addCoins, addXp, addRewards } = ctx;

  // 🛡️ منبع حقیقت: userId از سرور (نه کلاینت)
  const userId = res?.userId || user?.id || null;
  if (!userId) {
    return {
      successMessage: "پرداخت انجام شد اما کاربر یافت نشد",
      redirect: null,
      ok: false,
    };
  }

  const purpose = normalizePurpose(res?.purpose);
  const metadata = res?.metadata || {};
  const paymentId = res?.paymentId || null;
  const refId = res?.refId || null;
  const amount = Number(res?.amount ?? res?.amountToman ?? 0) || 0;
  const txId = refId || `tx_${paymentId || Date.now()}`;

  let redirect = null;
  let successMessage = "";

  // ═══════════════════════════════════════════════════════════════
  // ۰. پیش‌پردازش: کتاب کار (فعال‌سازی لایسنس قبل از switch)
  // ═══════════════════════════════════════════════════════════════
  const isWorkbook =
    metadata?.type === "workbook" && metadata?.bookId;

  if (isWorkbook) {
    try {
      await workbookService.activateLicense(
        userId,
        metadata.bookId,
        paymentId || refId
      );
      console.log("✅ لایسنس کتاب کار فعال شد:", metadata.bookId);
    } catch (e) {
      console.error("❌ خطا در فعال‌سازی لایسنس کتاب کار:", e);
      // شکست در لایسنس، پرداخت را باطل نمی‌کند
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ۱. switch اصلی بر اساس purpose (canonical)
  // ═══════════════════════════════════════════════════════════════
  try {
    switch (purpose) {
      // ─────────────────────────────────────────────────────────
      // سکه + XP
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.COINS: {
        // 🔧 FIX: سرور فیلدهای `coins` و `xp` را برمی‌گرداند (نه coinAmount/xpAmount)
        const coinAmount = Number(metadata.coins) || Number(metadata.coinAmount) || 0;
        const xpAmount = Number(metadata.xp) || Number(metadata.xpAmount) || 0;

        // 🔧 FIX: استفاده از addRewards (یک cooldown check برای هر دو) به‌جای
        // فراخوانی جداگانه addCoins + addXp (که دومی به‌خاطر cooldown رد می‌شود)
        if (coinAmount > 0 || xpAmount > 0) {
          if (typeof addRewards === 'function') {
            addRewards(coinAmount, xpAmount);
          } else {
            // fallback اگر addRewards موجود نبود
            if (coinAmount > 0) addCoins(coinAmount);
            if (xpAmount > 0) addXp(xpAmount);
          }
        }
        successMessage = `${coinAmount} سکه به حساب شما اضافه شد 🪙`;
        break;
      }

      // ─────────────────────────────────────────────────────────
      // اشتراک AI
      // سه حالت ممکن:
      //   ۱. type === "ai_quota"           → AI ساخت آزمون (پایه)
      //   ۲. plan starts with "pro-..."    → AI Chat پلن پرو
      //   ۳. plan starts with "ultimate-..." → AI Chat پلن ویژه
      //   ۴. legacy: plan === "monthly" یا "yearly" → اشتراک قدیمی
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.AI_SUBSCRIPTION: {
        const planRaw = metadata?.plan || "monthly";

        // ─── حالت ۱: AI ساخت آزمون ───
        if (metadata?.type === "ai_quota") {
          try {
            await activateAISubscription(userId, {
              plan: planRaw === "yearly" ? "yearly" : "monthly",
              months: metadata.months || (planRaw === "yearly" ? 12 : 1),
              paymentId: paymentId || refId,
            });
            successMessage = "اشتراک AI ساخت آزمون شما با موفقیت فعال شد ✨";
            console.log("✅ اشتراک AI Quota فعال شد:", planRaw);
          } catch (e) {
            console.error("❌ activateAISubscription error:", e);
            successMessage = "پرداخت انجام شد اما فعال‌سازی اشتراک با خطا مواجه شد";
          }
          break;
        }

        // ─── حالت ۲ و ۳: AI Chat (pro / ultimate) ───
        const isAIChat =
          planRaw.startsWith("pro-") || planRaw.startsWith("ultimate-");

        if (isAIChat) {
          try {
            const basePlan = planRaw.startsWith("pro-") ? "pro" : "ultimate";

            let days = 30;
            if (planRaw.endsWith("-daily")) days = 1;
            else if (planRaw.endsWith("-monthly")) days = 30;
            else if (planRaw.endsWith("-yearly")) days = 365;

            const expiresAt = Date.now() + days * 24 * 60 * 60 * 1000;

            const sub = {
              plan: basePlan,
              planRaw,
              startedAt: Date.now(),
              expiresAt,
              refId: refId || null,
              paymentId: paymentId || null,
            };

            // ✅ ذخیره در سرور (نه localStorage)
            const saved = await setAIChatSubscription(userId, sub);
            if (saved) {
              successMessage = `پلن ${basePlan === "pro" ? "پرو" : "ویژه"} شما با موفقیت فعال شد ✨`;
              console.log("✅ AI Chat subscription saved:", saved);
            } else {
              successMessage = "پرداخت انجام شد اما ذخیره‌سازی اشتراک با خطا مواجه شد";
            }
          } catch (e) {
            console.error("❌ AI Chat subscription error:", e);
            successMessage = "پرداخت انجام شد اما فعال‌سازی اشتراک با خطا مواجه شد";
          }
          break;
        }

        // ─── حالت ۴: legacy (monthly / yearly) ───
        try {
          const months = planRaw === "yearly" ? 12 : 1;
          const expiresAt = Date.now() + months * 30 * 24 * 60 * 60 * 1000;

          await setAIChatSubscription(userId, {
            plan: "pro",
            planRaw,
            startedAt: Date.now(),
            expiresAt,
            refId: refId || null,
            paymentId: paymentId || null,
          });

          successMessage = "اشتراک ویژه شما فعال شد ✨";
        } catch (e) {
          console.error("❌ legacy subscription error:", e);
          successMessage = "پرداخت انجام شد اما فعال‌سازی اشتراک با خطا مواجه شد";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // کتاب کار (لایسنس قبلاً فعال شد — اینجا فقط library)
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.WORKBOOK: {
        if (metadata.bookId) {
          try {
            // 🔧 FIX: از engine.set استفاده کن تا remoteSync آن را به سرور push کند
            const key = `user-library-${userId}`;
            const lib = engine.get(key, []) || [];
            if (!lib.includes(metadata.bookId)) {
              lib.push(metadata.bookId);
              engine.set(key, lib);
            }
          } catch (e) {
            console.warn("⚠️ library update failed:", e);
          }
        }
        successMessage = "کتاب کار شما فعال شد. در حال انتقال به صفحه مطالعه...";
        redirect = metadata.bookId ? `/workbook/${metadata.bookId}` : null;
        break;
      }

      // ─────────────────────────────────────────────────────────
      // 🆕 اشتراک درس (subscriptionManager)
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.LESSON_SUBSCRIPTION: {
        const { projectId, teacherId } = metadata;
        if (!projectId) {
          console.warn("⚠️ lesson_subscription: projectId یافت نشد");
          successMessage = "پرداخت انجام شد اما اطلاعات پروژه ناقص است";
          break;
        }

        try {
          const existing = subscriptionManager.getStatus(userId, projectId);
          const isRenew =
            existing?.status === "active" ||
            existing?.status === "expired";

          const subRes = isRenew
            ? subscriptionManager.renew({
                userId,
                projectId,
                amount,
                txId,
                gateway: "zarinpal",
              })
            : subscriptionManager.purchase({
                userId,
                projectId,
                teacherId: teacherId || null,
                amount,
                txId,
                gateway: "zarinpal",
              });

          if (!subRes?.ok && subRes?.error !== "duplicate_tx") {
            console.warn("⚠️ subscription fulfillment failed:", subRes?.error);
          }

          // emit برای رفرش UI
          engine.broadcastAndEmit("subscription:changed", {
            userId,
            projectId,
          });

          successMessage = isRenew
            ? "اشتراک شما با موفقیت تمدید شد ✨"
            : "اشتراک شما با موفقیت فعال شد 🎉";
        } catch (e) {
          console.error("❌ lesson subscription error:", e);
          successMessage = "پرداخت انجام شد اما فعال‌سازی اشتراک با خطا مواجه شد";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // 🆕 آنلاک آزمون مرحله (progressManager) + legacy exam
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.STEP_QUIZ_UNLOCK: {
        const { projectId, stepId, examId } = metadata;

        // مسیر مدرن: progressManager
        if (projectId && stepId) {
          try {
            progressManager.unlockQuizWithPayment(userId, projectId, stepId, {
              amount,
              txId,
              gateway: "zarinpal",
            });

            engine.broadcastAndEmit("quiz:unlocked", {
              userId,
              projectId,
              stepId,
            });

            successMessage = "تلاش اضافه برای آزمون فعال شد ✨";
          } catch (e) {
            console.error("❌ step quiz unlock error:", e);
            successMessage = "پرداخت انجام شد اما فعال‌سازی آزمون با خطا مواجه شد";
          }
        }

        // سازگاری عقب‌رو: آزمون قدیمی (examId)
        if (examId) {
          try {
            // 🔧 FIX: از engine.set
            const key = `user-exams-${userId}`;
            const list = engine.get(key, []) || [];
            if (!list.includes(examId)) {
              list.push(examId);
              engine.set(key, list);
            }
          } catch (e) {
            console.warn("⚠️ legacy exam save failed:", e);
          }
        }

        if (!successMessage) {
          successMessage = "پرداخت با موفقیت انجام شد ✨";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // خرید دوره
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.COURSE: {
        if (metadata.courseId) {
          try {
            // 🔧 FIX: از engine.set
            const key = `user-courses-${userId}`;
            const list = engine.get(key, []) || [];
            if (!list.includes(metadata.courseId)) {
              list.push(metadata.courseId);
              engine.set(key, list);
            }
            successMessage = "دوره با موفقیت به حساب شما اضافه شد ✨";
          } catch (e) {
            console.warn("⚠️ course save failed:", e);
          }
        } else {
          successMessage = "پرداخت با موفقیت انجام شد ✨";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // 🆕 جزوه ویژه (jozveService)
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.JOZVE: {
        try {
          const jozveService = (await import("../services/jozveService")).default;

          const jozveId = jozveService.makeId({
            grade: metadata.grade,
            field: metadata.field,
            book: metadata.book,
            chapterNumber: metadata.chapterNumber,
            lessonNumber: metadata.lessonNumber,
          });

          jozveService.activatePurchase(userId, jozveId, {
            grade: metadata.grade,
            field: metadata.field,
            book: metadata.book,
            chapterNumber: metadata.chapterNumber,
            lessonNumber: metadata.lessonNumber,
            title: metadata.title || "",
            coinPrice: metadata.coinPrice,
          });

          successMessage = "جزوه ویژه با موفقیت فعال شد ✨";
        } catch (e) {
          console.error("❌ jozve fulfillment error:", e);
          successMessage = "پرداخت انجام شد اما فعال‌سازی جزوه با خطا مواجه شد";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // 🆕 مطالعه آزاد (free_study)
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.FREE_STUDY: {
        try {
          const { courseId, videoId, bundle } = metadata;
          if (courseId) {
            // 🔧 FIX: از engine.get / engine.set
            const key = `user-free-study-${userId}`;
            const data = engine.get(key, {}) || {};
            if (!Array.isArray(data.courses)) data.courses = [];
            if (!Array.isArray(data.videos)) data.videos = [];

            if (bundle === true) {
              if (!data.courses.includes(courseId)) {
                data.courses.push(courseId);
              }
            } else if (videoId) {
              const vid = `${courseId}:${videoId}`;
              if (!data.videos.includes(vid)) {
                data.videos.push(vid);
              }
            }
            engine.set(key, data);
          }
          successMessage = "دسترسی شما فعال شد ✨";
        } catch (e) {
          console.error("❌ free_study fulfillment error:", e);
          successMessage = "پرداخت با موفقیت انجام شد ✨";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // 🆕 شارژ کیف پول (فقط refresh)
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.WALLET_CHARGE: {
        // سرور مبلغ را به کیف پول اضافه کرده — فقط refreshUser
        successMessage = "کیف پول شما با موفقیت شارژ شد 💳";
        break;
      }

      // ─────────────────────────────────────────────────────────
      // 🆕 کلاس خصوصی
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.PRIVATE_CLASS: {
        if (metadata.courseId) {
          try {
            // 🔧 FIX: از engine.set
            const key = `user-private-class-${userId}`;
            const list = engine.get(key, []) || [];
            if (!list.includes(metadata.courseId)) {
              list.push(metadata.courseId);
              engine.set(key, list);
            }
            successMessage = "ثبت‌نام کلاس خصوصی شما با موفقیت انجام شد ✨";
          } catch (e) {
            console.warn("⚠️ private_class save failed:", e);
          }
        } else {
          successMessage = "پرداخت با موفقیت انجام شد ✨";
        }
        break;
      }

      // ─────────────────────────────────────────────────────────
      // custom / default
      // ─────────────────────────────────────────────────────────
      case PAYMENT_PURPOSE.CUSTOM:
      default: {
        successMessage = "پرداخت با موفقیت انجام شد ✨";
        break;
      }
    }
  } catch (e) {
    // خطای fulfillment باعث شکست پرداخت نمی‌شود — فقط لاگ می‌شود
    console.error("❌ fulfillment unexpected error:", e);
    if (!successMessage) {
      successMessage = "پرداخت انجام شد اما اجرای عملیات با خطا مواجه شد";
    }
  }

  // ═══════════════════════════════════════════════════════════════
  // ۲. رفرش state کاربر (سکه، اشتراک، کیف پول و ...)
  // ═══════════════════════════════════════════════════════════════
  try {
    if (typeof refreshUser === "function") {
      await refreshUser();
    }
  } catch (e) {
    console.warn("⚠️ refreshUser error:", e);
  }

  return {
    ok: true,
    successMessage:
      successMessage ||
      `تراکنش «${getPurposeLabel(purpose)}» شما با موفقیت ثبت شد.`,
    redirect,
    purpose,
  };
}

// ================================================================
// MAIN COMPONENT
// ================================================================

export default function PaymentCallbackPage() {
  const navigate = useNavigate();
  const { user, refreshUser } = useAuth();
  const { addCoins, addXp, addRewards } = useCoins();

  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(true);
  const [fatalError, setFatalError] = useState(null);
  const [fulfillmentInfo, setFulfillmentInfo] = useState(null);

  const hasRunRef = useRef(false);

  // ===== بازگشت ایمن =====
  // در صفحه‌ی callback پرداخت، کاربر از درگاه بانک (خارجی) اومده
  // پس navigate(-1) می‌بره به صفحه‌ی بانک (expired) یا صفحه سیاه
  // فقط اگه referrer از خود اپ باشه، برگرد به قبلی
  const handleBack = () => {
    try {
      const sameOrigin =
        document.referrer &&
        document.referrer.includes(window.location.host);
      if (sameOrigin && window.history.length > 1) {
        navigate(-1);
      } else {
        navigate("/", { replace: true });
      }
    } catch {
      navigate("/", { replace: true });
    }
  };

  useEffect(() => {
    // 🛡️ Idempotency: جلوگیری از اجرای دوباره در React StrictMode
    if (hasRunRef.current) return;
    hasRunRef.current = true;

    const run = async () => {
      try {
        // ═══════════════════════════════════════════════════════
        // ۱. استخراج پارامترهای بازگشتی از درگاه
        // ═══════════════════════════════════════════════════════
        const { paymentId, authority, status } = parseCallbackParams();

        if (!paymentId || !authority) {
          setFatalError(
            "پارامترهای بازگشتی از درگاه ناقص هستند. لطفاً از پشتیبانی پیگیری کنید."
          );
          setLoading(false);
          return;
        }

        // ═══════════════════════════════════════════════════════
        // ۲. تأیید تراکنش از سمت سرور
        // ═══════════════════════════════════════════════════════
        const res = await verifyPayment({ paymentId, authority, status });
        setResult(res);

        if (!res?.success) {
          // پرداخت ناموفق — نیازی به fulfillment نیست
          setLoading(false);
          return;
        }

        // 🔧 FIX: idempotency — جلوگیری از اجرای دوباره fulfillment
        // دلیل ۱: سرور گفته تراکنش قبلاً پردازش شده (refresh صفحه)
        // دلیل ۲: در همین مرورگر قبلاً fulfillment انجام شده (tab دوم)
        const localKey = `payment-fulfilled-${paymentId}`;
        let alreadyFulfilledLocally = false;
        try {
          alreadyFulfilledLocally = !!localStorage.getItem(localKey);
        } catch {}

        if (res.alreadyVerified || alreadyFulfilledLocally) {
          console.log('[PaymentCallback] fulfillment skipped (idempotent):', {
            alreadyVerified: res.alreadyVerified,
            alreadyFulfilledLocally,
          });
          setFulfillmentInfo({
            ok: true,
            successMessage: `تراکنش «${getPurposeLabel(normalizePurpose(res.purpose))}» شما قبلاً با موفقیت پردازش شده بود.`,
            redirect: null,
            purpose: res.purpose,
          });
          setLoading(false);
          return;
        }

        // ═══════════════════════════════════════════════════════
        // ۳. اجرای fulfillment (تمام purposeها)
        // ═══════════════════════════════════════════════════════
        const info = await runFulfillment(res, {
          user,
          refreshUser,
          addCoins,
          addXp,
          addRewards,
        });

        // 🔧 FIX: علامت‌گذاری پرداخت به‌عنوان fulfill‌شده در این مرورگر
        try {
          localStorage.setItem(localKey, String(Date.now()));
        } catch {}

        setFulfillmentInfo(info);

        // ═══════════════════════════════════════════════════════
        // ۴. هدایت خودکار در صورت نیاز
        // ═══════════════════════════════════════════════════════
        if (info?.redirect) {
          setTimeout(() => {
            navigate(info.redirect, { replace: true });
          }, 1500);
        }
      } catch (err) {
        console.error("❌ verifyPayment خطا:", err);
        setFatalError(
          "خطا در ارتباط با سرور. در صورت کسر مبلغ، تا ۷۲ ساعت آینده به‌صورت خودکار بازگردانده می‌شود."
        );
      } finally {
        setLoading(false);
      }
    };

    run();
    
  }, []);

  // ============================================================
  //  حالت ۱: در حال بارگذاری
  // ============================================================
  if (loading) {
    return (
      <div className="payment-callback is-loading" dir="rtl">
        <div className="payment-card" role="status" aria-live="polite">
          <div className="payment-progress-bar" />
          <div className="payment-loading">
            <div className="payment-spinner" aria-hidden="true" />
            <p className="payment-loading-text">در حال تأیید پرداخت</p>
            <p className="payment-subtitle" style={{ marginTop: 4 }}>
              لطفاً صفحه را نبندید...
            </p>
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  //  حالت ۲: خطای غیرمنتظره
  // ============================================================
  if (fatalError) {
    return (
      <div className="payment-callback is-error" dir="rtl">
        <div className="payment-card" role="alert">
          <div className="payment-icon-wrap">
            <div className="payment-icon">
              <IconCross />
            </div>
          </div>

          <h2 className="payment-title">خطا در پردازش</h2>
          <p className="payment-subtitle">{fatalError}</p>

          <div className="payment-actions">
            <button
              className="payment-btn is-primary"
              onClick={() => navigate("/")}
              type="button"
            >
              <IconHome />
              بازگشت به خانه
            </button>
            <button
              className="payment-btn is-secondary"
              onClick={handleBack}
              type="button"
            >
              <IconBack />
              برگشت
            </button>
          </div>

          <div className="payment-footer">
            <IconShield />
            پشتیبانی ۲۴ ساعته: ۰۲۱-۱۲۳۴۵۶۷۸
          </div>
        </div>
      </div>
    );
  }

  // ============================================================
  //  حالت ۳: نتیجه از سرور
  // ============================================================
  const isSuccess = !!result?.success;
  const purposeLabel = getPurposeLabel(
    normalizePurpose(result?.purpose)
  );
  const isWorkbook =
    result?.metadata?.type === "workbook" && result?.metadata?.bookId;

  // پیام نمایشی: اگر fulfillment پیام مخصوص داده، از آن استفاده کن
  const displayMessage = isSuccess
    ? fulfillmentInfo?.successMessage ||
      (isWorkbook
        ? "کتاب کار شما فعال شد. در حال انتقال به صفحه مطالعه..."
        : `تراکنش «${purposeLabel}» شما با موفقیت ثبت شد.`)
    : result?.message ||
      "متأسفانه تراکنش انجام نشد. در صورت کسر مبلغ، طی ۷۲ ساعت آینده بازگردانده می‌شود.";

  return (
    <div
      className={`payment-callback ${isSuccess ? "is-success" : "is-error"}`}
      dir="rtl"
    >
      <div className="payment-card" role="alert" aria-live="polite">
        <div className="payment-icon-wrap">
          <div className="payment-icon">
            {isSuccess ? <IconCheck /> : <IconCross />}
          </div>
        </div>

        <h2 className="payment-title">
          {isSuccess ? "پرداخت با موفقیت انجام شد" : "پرداخت ناموفق بود"}
        </h2>
        <p className="payment-subtitle">{displayMessage}</p>

        {isSuccess && (
          <div className="payment-info">
            {!!result?.purpose && (
              <div className="payment-info-row">
                <span className="payment-info-label">نوع خرید</span>
                <span className="payment-info-value">{purposeLabel}</span>
              </div>
            )}

            {!!result?.refId && (
              <div className="payment-info-row">
                <span className="payment-info-label">کد رهگیری</span>
                <span className="payment-info-value">{result.refId}</span>
              </div>
            )}

            {Number(result?.amount) > 0 && (
              <div className="payment-info-row">
                <span className="payment-info-label">مبلغ پرداخت‌شده</span>
                <span className="payment-info-value is-amount">
                  {formatAmount(result.amount)}
                </span>
              </div>
            )}

            {!!result?.metadata?.bookId && (
              <div className="payment-info-row">
                <span className="payment-info-label">شناسه کتاب</span>
                <span className="payment-info-value">
                  {result.metadata.bookId}
                </span>
              </div>
            )}
          </div>
        )}

        <div className="payment-actions">
          {isSuccess ? (
            <>
              <button
                className="payment-btn is-primary"
                onClick={() => {
                  if (fulfillmentInfo?.redirect) {
                    navigate(fulfillmentInfo.redirect, { replace: true });
                  } else if (isWorkbook) {
                    navigate(`/workbook/${result.metadata.bookId}`, {
                      replace: true,
                    });
                  } else {
                    navigate("/");
                  }
                }}
                type="button"
              >
                <IconHome />
                {isWorkbook ? "باز کردن کتاب" : "بازگشت به خانه"}
              </button>
              <button
                className="payment-btn is-secondary"
                onClick={() => navigate("/profile/payments")}
                type="button"
              >
                <IconReceipt />
                تراکنش‌های من
              </button>
            </>
          ) : (
            <>
              <button
                className="payment-btn is-primary"
                onClick={handleBack}
                type="button"
              >
                <IconBack />
                تلاش دوباره
              </button>
              <button
                className="payment-btn is-secondary"
                onClick={() => navigate("/")}
                type="button"
              >
                <IconHome />
                خانه
              </button>
            </>
          )}
        </div>

        <div className="payment-footer">
          <IconShield />
          پرداخت امن از طریق درگاه بانکی
        </div>
      </div>
    </div>
  );
}